import PropTypes from 'prop-types';
import { Fragment } from 'react';
import styled from 'styled-components';

import { HorizontalList, VerticalList } from '~/components/Elements';
import { colors, spacing } from '~/utils/css-mixins';
import { money } from '~/utils/format';
import media from '~/utils/media';

import { findStartByLegStartNumber } from '../utils';
import AtgForm from './AtgForm';
import Start from './Start';

const SystemWrapper = styled.div`
  display: flex;
  flex-direction: column;
  font-size: 14px;
  line-height: 20px;

  > div + div {
    margin-top: ${spacing.siteGutter * 1.5}px;
  }

  ${media.tablet`
    flex-direction: row;

    > div + div {
      margin-top: 0;
      margin-left: ${spacing.siteGutter * 2}px;
    }
  `}

  ${media.giant`
    > div + div {
      margin-left: ${spacing.siteGutter * 3}px;
    }
  `}
`;

const SystemInfo = styled.div`
  margin-bottom: 12px;
`;

const Header = styled.h3`
  font-size: 16px;
  margin-bottom: 5px;
`;

const StyledVerticalList = styled(VerticalList)`
  li {
    font-size: 14px;
    line-height: 23px;

    ${media.tablet`
      font-size: 16px;
      line-height: 28px;
    `}
  }
`;

const StyledHorizontalList = styled(HorizontalList)`
  display: inline;

  li:not(:last-child)::after {
    content: ', ';
  }
`;

const StyledReserveList = styled(StyledHorizontalList)`
  color: ${colors.textmeta};

  &::before {
    content: '(';
  }

  &::after {
    content: ')';
  }
`;

const Info = styled.p`
  margin-top: 5px;
  color: ${colors.textmeta};
  font-size: 14px;
  font-style: italic;
  line-height: 20px;
`;

const System = ({
  round,
  system: { cost, data, type, atg_file, auto_updated, number_of_rows, v6 },
}) => (
  <Fragment>
    <SystemInfo>
      <Header>{type === 'small' ? 'Lilla' : 'Stora'} systemet</Header>
      <div>
        {money(cost, true)} ({number_of_rows} rader)
      </div>
    </SystemInfo>

    <SystemInfo>
      <StyledVerticalList>
        {Object.keys(data) // Legs are keys of system.data
          .sort((a, b) => a > b) // Sort by ascending leg
          .map(leg => Number.parseInt(leg, 10)) // Easier to work with leg as number
          .map(leg => (
            <li key={leg}>
              <strong>
                {round.bet_type}-{leg}:{' '}
              </strong>
              <StyledHorizontalList>
                {data[leg].regular.map((startNumber, idx, arr) => (
                  <li key={startNumber}>
                    <Start
                      start={findStartByLegStartNumber(round, leg, startNumber)}
                      showName={arr.length === 1}
                    />
                  </li>
                ))}
              </StyledHorizontalList>{' '}
              <StyledReserveList>
                {data[leg].reserves.map(startNumber => (
                  <li key={startNumber}>
                    <Start
                      start={findStartByLegStartNumber(round, leg, startNumber)}
                      showName={false}
                    />
                  </li>
                ))}
              </StyledReserveList>
            </li>
          ))}
      </StyledVerticalList>
    </SystemInfo>

    {atg_file && <AtgForm xml={atg_file} />}

    {v6 && <Info>Endast spel i sexrättspotten (V6)</Info>}

    {auto_updated && (
      <Info>
        OBS: På grund av strykning/strykningar har det ursprungliga systemet
        justerats.
      </Info>
    )}
  </Fragment>
);

const SystemList = ({ round, systems }) => (
  <SystemWrapper>
    {systems.map(system => (
      <div key={system.type}>
        <System round={round} system={system} />
      </div>
    ))}
  </SystemWrapper>
);

SystemList.propTypes = {
  systems: PropTypes.array.isRequired,
  round: PropTypes.object.isRequired,
};

export default SystemList;
